import { useCallback } from 'react';
import { keepPreviousData, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as effortsApi from '../api/effortsApi.js';
import { useEffortsFilters } from './useEffortsFilters.js';
import { useUploadProgress } from './useUploadProgress.js';

const EFFORTS_QUERY_KEY = 'efforts-data';
const EFFORTS_SUMMARY_QUERY_KEY = 'efforts-summary'
const EFFORTS_BY_GROUP_QUERY_KEY = 'efforts-by-group';

/** Mean of a numeric field across rows, rounded to 1 decimal. 0 for no rows. */
function avgBy(rows, selector) {
  if (!rows.length) return 0;
  const sum = rows.reduce((acc, r) => acc + (Number(selector(r)) || 0), 0);
  return Math.round((sum / rows.length) * 10) / 10;
}

/**
 * Build the four aggregate KPI tiles from the field-rep rows: average working
 * days, average Dr call, average coverage %, and average compliance %.
 * @param {Object[]} rows - EffortsRow[] (see source efforts.types.js)
 * @returns {Object[]} EffortsKpi[]
 */
function deriveEffortsKpis(rows) {
  return [
    { id: 'workingDays', label: 'Avg Working Days', value: avgBy(rows, (r) => r.workingDays), format: 'number' },
    { id: 'drCallAverage', label: 'Dr Call Average', value: avgBy(rows, (r) => r.drCallAverage), format: 'number' },
    { id: 'coverage', label: 'Coverage %', value: avgBy(rows, (r) => r.coveragePercent), format: 'percent' },
    { id: 'compliance', label: 'Compliance %', value: avgBy(rows, (r) => r.compliancePercent), format: 'percent' },
  ];
}

/**
 * TanStack Query hook for the Efforts field-rep data. Reads the shared filters
 * and returns the row-level table plus the four aggregate KPIs derived from
 * those rows (client-side; the mock backend returns no precomputed KPIs).
 */
export function useEffortsDashboardSummary() {
  const filters = useEffortsFilters((s) => s.applied);
  return useQuery({
    queryKey: [EFFORTS_SUMMARY_QUERY_KEY, filters],
    queryFn: () => effortsApi.getEffortsDataSummary(filters),
    select: (data) => data?.kpis ?? [],
  });
}

/**
 * TanStack Query hook for the Efforts field-rep data. Reads the shared filters
 * and returns the current page of field-rep rows plus the four aggregate KPIs
 * derived from those rows, along with the pagination envelope
 * (`page`/`page_size`/`total`/`total_pages`) so the page can render a pager.
 * @param {number} [page=1]
 * @param {number} [pageSize=10] - Backend max is 100; the API clamps to [1,100].
 */
export function useEffortsDashboard(page = 1, pageSize = 10) {
  const filters = useEffortsFilters((s) => s.applied);
  return useQuery({
    queryKey: [EFFORTS_QUERY_KEY, filters, page, pageSize],
    queryFn: () => effortsApi.getEffortsData(filters, page, pageSize),
    placeholderData: keepPreviousData,
    select: (data) => {
      const rows = data?.rows ?? [];
      return {
        rows,
        kpis: deriveEffortsKpis(rows),
        page: data?.page ?? page,
        pageSize: data?.page_size ?? pageSize,
        total: data?.total ?? rows.length,
        totalPages: data?.total_pages ?? 1,
      };
    },
  });
}

const EFFORTS_BAND_COUNTS_QUERY_KEY = 'efforts-band-counts';
const EFFORTS_INSIGHTS_QUERY_KEY = 'efforts-insights';
const EFFORTS_DESIGNATION_TREND_QUERY_KEY = 'efforts-designation-trend';
const EFFORTS_FLS_TREND_QUERY_KEY = 'efforts-fls-trend';
const EFFORTS_MANAGERS_TREND_QUERY_KEY = 'efforts-managers-trend';
const EFFORTS_MANAGERS_EMP_TREND_QUERY_KEY = 'efforts-managers-emp-trend';
const EFFORTS_CORRELATION_QUERY_KEY = 'efforts-correlation';

/**
 * KPI band counts for the 4 top cards (each with 4 bucket counts).
 */
export function useEffortsBandCounts({ enabled = true } = {}) {
  const filters = useEffortsFilters((s) => s.applied);
  return useQuery({
    queryKey: [EFFORTS_BAND_COUNTS_QUERY_KEY, filters],
    queryFn: () => effortsApi.getBandCounts(filters),
    enabled,
    select: (data) => ({
      cards: data?.cards ?? [],
      employeeCount: data?.employeeCount ?? 0,
    }),
  });
}

/**
 * Auto-generated insight lines derived from band counts + summary.
 */
export function useEffortsInsights({ enabled = true } = {}) {
  const filters = useEffortsFilters((s) => s.applied);
  return useQuery({
    queryKey: [EFFORTS_INSIGHTS_QUERY_KEY, filters],
    queryFn: () => effortsApi.getInsights(filters),
    enabled,
    select: (data) => data?.insights ?? [],
  });
}

/**
 * Monthly trend grouped by designation (FLM/SLM/TLM), Apr-Mar.
 * @param {string} [fiscalYear] - e.g. "2025-26". Omit to use latest.
 */
export function useEffortsDesignationTrend(fiscalYear) {
  const filters = useEffortsFilters((s) => s.applied);
  return useQuery({
    queryKey: [EFFORTS_DESIGNATION_TREND_QUERY_KEY, fiscalYear, filters],
    queryFn: () => effortsApi.getDesignationTrend(fiscalYear, filters),
    select: (data) => ({
      fiscalYear: data?.fiscalYear ?? '',
      months: data?.months ?? [],
      series: data?.series ?? [],
    }),
  });
}

/**
 * Per-employee monthly Dr Coverage % and Freq Compliance % trend, Apr-Mar.
 * @param {string} [fiscalYear]
 */
export function useEffortsFlsTrend(fiscalYear) {
  const filters = useEffortsFilters((s) => s.applied);
  return useQuery({
    queryKey: [EFFORTS_FLS_TREND_QUERY_KEY, fiscalYear, filters],
    queryFn: () => effortsApi.getFlsTrend(fiscalYear, filters),
    select: (data) => ({
      fiscalYear: data?.fiscalYear ?? '',
      months: data?.months ?? [],
      employees: data?.employees ?? [],
    }),
  });
}

/**
 * Monthly manager-tier (FLM/SLM/TLM) trend, Apr-Mar. The tier is selected in
 * the tab (not via the shared role filter), so it is a hook argument.
 * @param {string} [tier='FLM'] - FLM | SLM | TLM
 * @param {string} [fiscalYear]
 */
export function useEffortsManagersTrend(tier = 'FLM', fiscalYear) {
  const filters = useEffortsFilters((s) => s.applied);
  return useQuery({
    queryKey: [EFFORTS_MANAGERS_TREND_QUERY_KEY, tier, fiscalYear, filters],
    queryFn: () => effortsApi.getManagersTrend(tier, fiscalYear, filters),
    select: (data) => ({
      tier: data?.tier ?? tier,
      fiscalYear: data?.fiscalYear ?? '',
      months: data?.months ?? [],
      points: data?.points ?? [],
    }),
  });
}

/**
 * Per-manager (one row per FLM/SLM/TLM) monthly Field Working Days + Dr. Call
 * Avg, Apr-Mar. Backs the reworked Managers Trend tab (the new per-employee
 * endpoint). Tier is a tab-local selector, so it is a hook argument.
 * @param {string} [tier='FLM'] - FLM | SLM | TLM
 * @param {string} [fiscalYear]
 */
export function useEffortsManagersEmployeeTrend(tier = 'FLM', fiscalYear) {
  const filters = useEffortsFilters((s) => s.applied);
  return useQuery({
    queryKey: [EFFORTS_MANAGERS_EMP_TREND_QUERY_KEY, tier, fiscalYear, filters],
    queryFn: () => effortsApi.getManagersEmployeeTrend(tier, fiscalYear, filters),
    select: (data) => ({
      tier: data?.tier ?? tier,
      fiscalYear: data?.fiscalYear ?? '',
      months: data?.months ?? [],
      employees: data?.employees ?? [],
    }),
  });
}

/**
 * Effort vs sales-performance correlation (2x2 quadrant analysis). The
 * effort-axis / threshold choices are tab-local, so they are hook arguments;
 * the applied efforts filters feed the query key for refetch on Apply.
 *
 * @param {{ effortAxis?: string, salesThreshold?: number, effortThreshold?: number }} [options]
 */
export function useEffortsCorrelation({
  effortAxis = 'coverage_percent',
  salesThreshold,
  effortThreshold,
} = {}) {
  const filters = useEffortsFilters((s) => s.applied);
  return useQuery({
    queryKey: [
      EFFORTS_CORRELATION_QUERY_KEY,
      effortAxis,
      salesThreshold ?? null,
      effortThreshold ?? null,
      filters,
    ],
    queryFn: () =>
      effortsApi.getCorrelation({ effortAxis, salesThreshold, effortThreshold }, filters),
    select: (data) => ({
      effortAxis: data?.effortAxis ?? effortAxis,
      salesThreshold: data?.salesThreshold ?? null,
      effortThreshold: data?.effortThreshold ?? null,
      quadrants: data?.quadrants ?? {
        highEffortHighSales: 0,
        highEffortLowSales: 0,
        lowEffortHighSales: 0,
        lowEffortLowSales: 0,
      },
      rows: data?.rows ?? [],
      employeeCount: data?.employeeCount ?? 0,
    }),
  });
}

/**
 * Effort grouped by division/state/hq (latest month totals).
 * @param {string} [groupBy] - division | state | hq
 */
export function useEffortsByGroup(groupBy = 'division') {
  const filters = useEffortsFilters((s) => s.applied);
  return useQuery({
    queryKey: [EFFORTS_BY_GROUP_QUERY_KEY, groupBy, filters],
    queryFn: () => effortsApi.getByGroup(groupBy, filters),
    select: (data) => ({
      groupBy: data?.groupBy ?? groupBy,
      months: data?.months ?? [],
      groups: data?.groups ?? [],
    }),
  });
}

const EFFORTS_BAND_EMPLOYEES_QUERY_KEY = 'efforts-band-employees';

/**
 * Drill-down list of field users within a single band of one of the four top
 * KPI cards. Reads the shared filters so the drill-down matches the card
 * counts. Only runs when `enabled` is true (i.e. the modal is open) and a
 * metric/band pair is provided.
 *
 * @param {string|null} metricId - Card id (workingDays|drCallAverage|coverage|compliance)
 * @param {number|null} band - Band index 0..3
 * @param {{ enabled?: boolean }} [options]
 */
export function useEffortsBandEmployees(metricId, band, { enabled = true } = {}) {
  const filters = useEffortsFilters((s) => s.applied);
  const isEnabled = Boolean(enabled && metricId != null && band != null);
  return useQuery({
    queryKey: [EFFORTS_BAND_EMPLOYEES_QUERY_KEY, metricId, band, filters],
    queryFn: () => effortsApi.getBandEmployees(metricId, band, filters),
    enabled: isEnabled,
    select: (data) => ({
      metric: data?.metric ?? '',
      band: data?.band ?? band,
      bandLabel: data?.bandLabel ?? '',
      total: data?.total ?? 0,
      employees: data?.employees ?? [],
    }),
  });
}

const EFFORTS_PERIODS_QUERY_KEY = 'efforts-periods';
const EFFORTS_FILTER_OPTIONS_QUERY_KEY = 'efforts-filter-options';
const EFFORTS_INSIGHT_EMPLOYEES_QUERY_KEY = 'efforts-insight-employees';

/** Pull a flat string[] of option labels from a cascade list (string[] or {label}/{id}[]). */
function toLabelList(list) {
  return (Array.isArray(list) ? list : [])
    .map((o) => (o == null ? '' : typeof o === 'object' ? o.label ?? o.id ?? o.value ?? '' : o))
    .filter(Boolean);
}

/**
 * Available periods for the Year / Period filters.
 * @returns {{ fiscalYears: any[], latest: string|null, isLoading: boolean }}
 */
export function useEffortsPeriods() {
  const query = useQuery({
    queryKey: [EFFORTS_PERIODS_QUERY_KEY],
    queryFn: () => effortsApi.getPeriods(),
    staleTime: 10 * 60 * 1000,
    select: (data) => ({
      fiscalYears: data?.fiscalYears ?? [],
      latest: data?.latest ?? null,
    }),
  });
  return {
    fiscalYears: query.data?.fiscalYears ?? [],
    latest: query.data?.latest ?? null,
    isLoading: query.isLoading || query.isFetching,
  };
}

/**
 * Cascading filter options (division/state/HQ). The UPSTREAM cascade is kept —
 * State is narrowed by the selected Divisions, HQ by Divisions + States — but
 * no list is ever narrowed by its OWN selection, so ticking an item never
 * shrinks that dropdown to only the ticked values.
 *
 * This is achieved with three independent option requests, each omitting the
 * dropdown's own selection:
 *   - divisions : requested with neither division nor state  (always all)
 *   - states    : requested with the selected divisions only (not state)
 *   - hqs       : requested with divisions + states
 *
 * The caller passes the current draft `yearmonth`, `division` and `state` so
 * the lists reflect pending (not yet applied) choices.
 *
 * @param {{ yearmonth?: string|null, division?: string[], state?: string[] }} [draft]
 */
export function useEffortsFilterOptions({ yearmonth = null, division = [], state = [] } = {}) {
  const ym = yearmonth ?? '';
  const divisionCsv = [...division].sort().join(',');
  const stateCsv = [...state].sort().join(',');

  // Division list: independent of any division/state selection → always all.
  const divisionQuery = useQuery({
    queryKey: [EFFORTS_FILTER_OPTIONS_QUERY_KEY, 'divisions', ym],
    queryFn: () => effortsApi.getFilterOptions({ yearmonth }),
    staleTime: 5 * 60 * 1000,
    placeholderData: keepPreviousData,
    select: (data) => ({ list: toLabelList(data?.divisions), stats: data?.divisionStats ?? {} }),
  });

  // State list: narrowed by selected divisions only (NOT by its own selection).
  const stateQuery = useQuery({
    queryKey: [EFFORTS_FILTER_OPTIONS_QUERY_KEY, 'states', ym, divisionCsv],
    queryFn: () => effortsApi.getFilterOptions({ yearmonth, division }),
    staleTime: 5 * 60 * 1000,
    placeholderData: keepPreviousData,
    select: (data) => ({ list: toLabelList(data?.states), stats: data?.stateStats ?? {} }),
  });

  // HQ list: narrowed by selected divisions + states (its upstream cascade).
  const hqQuery = useQuery({
    queryKey: [EFFORTS_FILTER_OPTIONS_QUERY_KEY, 'hqs', ym, divisionCsv, stateCsv],
    queryFn: () => effortsApi.getFilterOptions({ yearmonth, division, state }),
    staleTime: 5 * 60 * 1000,
    placeholderData: keepPreviousData,
    select: (data) => ({ list: toLabelList(data?.hqs), stats: data?.hqStats ?? {} }),
  });

  return {
    divisions: divisionQuery.data?.list ?? [],
    states: stateQuery.data?.list ?? [],
    territories: hqQuery.data?.list ?? [],
    // Per-option stats maps keyed by option name (flsCount, avgWorkingDays,
    // coveragePercent). Empty object when the backend sends none.
    divisionStats: divisionQuery.data?.stats ?? {},
    stateStats: stateQuery.data?.stats ?? {},
    territoryStats: hqQuery.data?.stats ?? {},
    divisionsLoading: divisionQuery.isLoading || divisionQuery.isFetching,
    statesLoading: stateQuery.isLoading || stateQuery.isFetching,
    territoriesLoading: hqQuery.isLoading || hqQuery.isFetching,
    isLoading: divisionQuery.isLoading || stateQuery.isLoading || hqQuery.isLoading,
  };
}

/**
 * Drill-down list of field users for a preset insight. Only runs when enabled
 * (modal open) and a preset is provided. Reuses the applied efforts filters.
 *
 * @param {string|null} preset
 * @param {{ enabled?: boolean }} [options]
 */
export function useEffortsInsightEmployees(preset, { enabled = true } = {}) {
  const filters = useEffortsFilters((s) => s.applied);
  const isEnabled = Boolean(enabled && preset);
  return useQuery({
    queryKey: [EFFORTS_INSIGHT_EMPLOYEES_QUERY_KEY, preset, filters],
    queryFn: () => effortsApi.getInsightEmployees(preset, filters),
    enabled: isEnabled,
    select: (data) => ({
      insight: data?.insight ?? '',
      metricLabel: data?.metricLabel ?? 'Value',
      metricFormat: data?.metricFormat ?? 'number',
      total: data?.total ?? 0,
      employees: data?.employees ?? [],
    }),
  });
}

/**
 * Applied Efforts multi-select filter selection plus the commit/reset actions.
 * `applied.{division,state,territory}` are arrays. The filter bar keeps its own
 * draft selection locally and calls `applySelection` on Apply (so the dashboard
 * hooks only refetch when Apply is clicked), or `resetSelection` on Reset.
 */
export function useEffortsFilterState() {
  const applied = useEffortsFilters((s) => s.applied);
  const applySelection = useEffortsFilters((s) => s.applySelection);
  const resetSelection = useEffortsFilters((s) => s.resetSelection);
  return { applied, applySelection, resetSelection };
}

/**
 * File upload hook for the Efforts dashboard. Wraps effortsApi.uploadFile with
 * a TanStack mutation and pushes progress into the Zustand upload store.
 * Invalidates the efforts-data query on success so the table refreshes.
 *
 * @returns {{
 *   upload: (file: File) => void,
 *   isUploading: boolean,
 *   progress: number,
 *   error: string | null,
 * }}
 * 
 * 
 */
export function useFileUpload() {
  const queryClient = useQueryClient();
  const { isUploading, progress, error, startUpload, setProgress, finishUpload, failUpload } =
    useUploadProgress();

  const mutation = useMutation({
    mutationFn: (file) => {
      startUpload();
      return effortsApi.uploadFile(file, (pct) => setProgress(pct));
    },
    onSuccess: () => {
      finishUpload();
      queryClient.invalidateQueries({ queryKey: [EFFORTS_QUERY_KEY] });
    },
    onError: (err) => {
      failUpload(err?.message || 'Upload failed');
    },
  });

  const upload = useCallback((file) => mutation.mutate(file), [mutation]);

  return { upload, isUploading, progress, error };
}
